import React, { useMemo } from 'react';
import { Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { selectStylesSm } from '~/Styles/reactSelectStyles';

const BANCOS_OPTIONS = [
  { value: 'Todos', label: 'Todos' },
  { value: 'CAIXA', label: 'CAIXA' },
  { value: 'ITAU', label: 'ITAÚ' },
  { value: 'BRADESCO', label: 'BRADESCO' },
];

const MODOS_OPTIONS = [
  { value: 'boleto', label: 'Boleto' },
  { value: 'grafica', label: 'Gráfica' },
];

const schema = Yup.object({
  dataInicial: Yup.date()
    .required('Período inicial deve ser informado.')
    .typeError('Data informada é inválida.'),
  dataFinal: Yup.date()
    .required('Período final deve ser informado.')
    .typeError('Data informada é inválida.')
    .min(Yup.ref('dataInicial'), 'Período inicial deve ser menor ou igual ao período final.'),
  banco: Yup.string()
    .oneOf(['Todos', 'CAIXA', 'ITAU', 'BRADESCO'])
    .required('Banco é obrigatório.'),
  codigoConvenio: Yup.number().nullable(),
  modo: Yup.string().oneOf(['boleto', 'grafica']).required('Modo é obrigatório.'),
});

const initialValues = {
  dataInicial: '',
  dataFinal: '',
  banco: 'Todos',
  codigoConvenio: null,
  modo: 'boleto',
};

const FormFiltros = ({ bancos, onSubmit, loading }) => {
  const conveniosOptions = useMemo(
    () =>
      bancos
        ? [
            { value: null, label: 'Todos os convênios' },
            ...bancos.map((b) => ({
              value: b.codigo_cliente,
              label: `${b.codigo_cliente} - ${b.descricao}`,
            })),
          ]
        : [{ value: null, label: 'Todos os convênios' }],
    [bancos]
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, resetForm }) => {
        const diff =
          values.dataInicial && values.dataFinal
            ? (new Date(values.dataFinal) - new Date(values.dataInicial)) /
              (1000 * 60 * 60 * 24)
            : 0;
        const alertaPeriodo = diff > 90;

        return (
          <Form noValidate onSubmit={handleSubmit} className="form-filtros">
            <Row>
              <Col xs={12} md={3}>
                <Form.Group controlId="dataInicial">
                  <Form.Label className="small mb-1">Período Inicial *</Form.Label>
                  <Form.Control
                    type="date"
                    name="dataInicial"
                    value={values.dataInicial}
                    onChange={handleChange}
                    isInvalid={touched.dataInicial && !!errors.dataInicial}
                    size="sm"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.dataInicial}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group controlId="dataFinal">
                  <Form.Label className="small mb-1">Período Final *</Form.Label>
                  <Form.Control
                    type="date"
                    name="dataFinal"
                    value={values.dataFinal}
                    onChange={handleChange}
                    isInvalid={touched.dataFinal && !!errors.dataFinal}
                    size="sm"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.dataFinal}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group controlId="banco">
                  <Form.Label className="small mb-1">Banco</Form.Label>
                  <Select
                    inputId="banco"
                    options={BANCOS_OPTIONS}
                    value={BANCOS_OPTIONS.find((o) => o.value === values.banco) || null}
                    onChange={(opt) => setFieldValue('banco', opt ? opt.value : 'Todos')}
                    styles={selectStylesSm}
                    placeholder="Selecione..."
                  />
                  {touched.banco && errors.banco && (
                    <div className="text-danger" style={{ fontSize: '0.8rem' }}>
                      {errors.banco}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} md={3}>
                <Form.Group controlId="codigoConvenio">
                  <Form.Label className="small mb-1">Convênio</Form.Label>
                  <Select
                    inputId="codigoConvenio"
                    options={conveniosOptions}
                    value={
                      conveniosOptions.find((o) => o.value === values.codigoConvenio) ||
                      conveniosOptions[0]
                    }
                    onChange={(opt) =>
                      setFieldValue('codigoConvenio', opt ? opt.value : null)
                    }
                    styles={selectStylesSm}
                    placeholder="Todos os convênios"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={3}>
                <Form.Group controlId="modo">
                  <Form.Label className="small mb-1">Modo *</Form.Label>
                  <Select
                    inputId="modo"
                    options={MODOS_OPTIONS}
                    value={MODOS_OPTIONS.find((o) => o.value === values.modo) || null}
                    onChange={(opt) => setFieldValue('modo', opt ? opt.value : 'boleto')}
                    styles={selectStylesSm}
                    placeholder="Selecione o modo..."
                  />
                  {touched.modo && errors.modo && (
                    <div className="text-danger" style={{ fontSize: '0.8rem' }}>
                      {errors.modo}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {alertaPeriodo && (
              <Alert variant="warning" className="py-2">
                Períodos longos podem demorar a gerar.
              </Alert>
            )}

            <div className="d-flex botoes-acao">
              <Button type="submit" variant="primary" size="sm" disabled={loading} className="mr-2">
                {loading ? 'Gerando...' : 'Gerar Relatório'}
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                onClick={() => resetForm()}
                disabled={loading}
              >
                Limpar
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default FormFiltros;
